import React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ConfidenceLegend from "./ConfidenceLegend";

const dotColor = (c) => {
  if (c >= 0.75) return "#2e9b5b";
  if (c >= 0.5) return "#e0952f";
  return "#b0b0b0";
};

const PROCESSING_MESSAGES = [
  "Reading between the lines...",
  "Getting to know you...",
  "Picking up on the little things...",
  "Finding what makes you you...",
  "Listening closely...",
  "Connecting the dots...",
  "Making sense of your story...",
  "Noticing the details...",
  "Learning what matters to you...",
  "Tuning into your vibe...",
  "Capturing your essence...",
  "Turning your words into a profile...",
  "Understanding your world...",
  "Piecing it all together...",
  "Taking it all in...",
  "Getting the full picture...",
  "Admiring your good taste...",
  "Mapping out who you are...",
  "Soaking up every word...",
  "Figuring out your type...",
  "Decoding your dealbreakers...",
  "Bottling up your charm...",
  "Finding your best angles...",
  "Cataloging your quirks...",
  "Warming up the matchmaker...",
  "Pulling out the highlights...",
  "Getting a read on you...",
  "Highlighting what makes you special...",
  "Setting the scene for great matches...",
  "Almost there...",
];

const randomMessageIndex = () =>
  Math.floor(Math.random() * PROCESSING_MESSAGES.length);

const speechSupported =
  typeof window !== "undefined" &&
  Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

const Capture = ({
  aboutMe,
  lookingFor,
  setAboutMe,
  setLookingFor,
  meStatus,
  lookStatus,
  meChips,
  lookChips,
  limitReached,
  onDeleteTrait,
  onSubmitPrompt,
  onBack,
  onDone,
}) => {
  const [tab, setTab] = React.useState("me");
  const [recording, setRecording] = React.useState(false);
  const [msgIndex, setMsgIndex] = React.useState(randomMessageIndex);
  const recognitionRef = React.useRef(null);
  const baseTextRef = React.useRef("");

  const isMe = tab === "me";
  const text = isMe ? aboutMe : lookingFor;
  const setText = isMe ? setAboutMe : setLookingFor;
  const status = isMe ? meStatus : lookStatus;
  const spinning = status === "submitting" || status === "processing";
  const done = status === "done";
  const locked = spinning || done;
  const disabled = locked || limitReached;
  const submittedAny = meStatus !== "idle" || lookStatus !== "idle";
  const chips = isMe ? meChips || [] : lookChips || [];
  const promptType = isMe ? "self_perception" : "partner_preference";

  const overlaySx = {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    borderRadius: 1,
    bgcolor: "rgba(255,255,255,0.82)",
  };

  React.useEffect(() => {
    if (!spinning) return undefined;
    const id = setInterval(() => {
      setMsgIndex((prev) => {
        let next = randomMessageIndex();
        if (next === prev) next = (next + 1) % PROCESSING_MESSAGES.length;
        return next;
      });
    }, 3000);
    return () => clearInterval(id);
  }, [spinning]);

  const placeholder = isMe
    ? "What should we know about you? Food, hobbies, dealbreakers, the small stuff. There's no wrong way to do this."
    : "Describe the kind of person you're hoping to meet. Values, habits, vibes, whatever feels right.";

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const startRecording = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    baseTextRef.current = text ? text.trim() + " " : "";

    recognition.onresult = (event) => {
      let sessionText = "";
      for (let i = 0; i < event.results.length; i++) {
        sessionText += event.results[i][0].transcript;
      }
      setText(baseTextRef.current + sessionText);
    };
    recognition.onend = () => {
      setRecording(false);
      recognitionRef.current = null;
    };
    recognition.onerror = () => {
      setRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
  };

  const toggleRecording = () => {
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const switchTab = (next) => {
    if (!next || next === tab) return;
    if (recording) stopRecording();
    setTab(next);
  };

  const handleSubmit = () => {
    if (recording) stopRecording();
    onSubmitPrompt(tab);
  };

  React.useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  const tabIndicator = (tabStatus) => {
    if (tabStatus === "submitting" || tabStatus === "processing") {
      return <CircularProgress size={13} color="inherit" />;
    }
    if (tabStatus === "done") {
      return <CheckCircleIcon sx={{ fontSize: 15, color: "#2e9b5b" }} />;
    }
    return null;
  };

  const tabLabel = (label, tabStatus) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {label}
      {tabIndicator(tabStatus)}
    </Box>
  );

  return (
    <Box sx={{ p: 2.5, maxWidth: 560, mx: "auto", pb: 6 }}>
      <Tabs
        value={tab}
        onChange={(e, next) => switchTab(next)}
        variant="fullWidth"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="me" label={tabLabel("About me", meStatus)} />
        <Tab value="look" label={tabLabel("Looking for", lookStatus)} />
      </Tabs>

      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
        {isMe
          ? "Tell us about you. Food, hobbies, dealbreakers. The small stuff matters."
          : "Now describe who you're hoping to meet. Values, vibes, habits."}
      </Typography>

      <Box sx={{ position: "relative" }}>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          multiline
          minRows={8}
          fullWidth
          disabled={disabled}
        />
        {limitReached ? (
          <Box sx={overlaySx}>
            <AccessTimeIcon sx={{ fontSize: 28, color: "text.secondary" }} />
            <Typography
              sx={{
                fontSize: 13,
                color: "text.secondary",
                textAlign: "center",
                px: 3,
              }}
            >
              You've reached today's limit. Come back tomorrow to add more.
            </Typography>
          </Box>
        ) : locked ? (
          <Box sx={overlaySx}>
            {done ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 30, color: "#2e9b5b" }} />
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Added to your profile
                </Typography>
              </>
            ) : (
              <>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {PROCESSING_MESSAGES[msgIndex]}
                </Typography>
              </>
            )}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
          gap: 1,
          minHeight: 40,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          {text.trim().length} chars
        </Typography>
        {disabled ? null : speechSupported ? (
          <Button
            className="tnfm-pill-btn"
            variant={recording ? "contained" : "outlined"}
            color={recording ? "error" : "primary"}
            startIcon={recording ? <StopIcon /> : <MicIcon />}
            onClick={toggleRecording}
          >
            {recording ? "Stop" : "Talk"}
          </Button>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            Voice input isn't supported here. Type instead.
          </Typography>
        )}
      </Box>

      {done && chips.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
            Here's what we picked up. Remove anything that's off.
          </Typography>
          <ConfidenceLegend sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {chips.map((t) => (
              <Chip
                key={t.id}
                label={t.value}
                onDelete={() => onDeleteTrait(promptType, t.id)}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: dotColor(t.confidence),
                      ml: 1,
                    }}
                  />
                }
                sx={{ fontSize: 13 }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      <Box sx={{ display: "flex", gap: 1.5, mt: 4 }}>
        <Button
          className="tnfm-pill-btn"
          variant="outlined"
          fullWidth
          onClick={submittedAny ? onDone : onBack}
        >
          {submittedAny ? "Done" : "Back"}
        </Button>
        <Button
          className="tnfm-pill-btn"
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!text.trim() || disabled}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default Capture;
